import React, {
  useState,
  useContext,
  useEffect
} from 'react';

import PropTypes from 'prop-types';

import {
  checkInputValidity
} from 'src/Utility/utility';

import {
  SalesContext
} from 'src/Context/SalesContext';

import VettingDropDown from 'src/Components/Common/VettingDropDown/VettingDropDown';

import VettingInput from 'src/Components/Common/VettingInput/VettingInput';

import {
  FormFieldLabel
} from 'src/StyledComponents';

import {
  provinces
} from 'src/Common/constants';

import './InstallationAddressEditor.css';

const InstallationAddressEditor = (props) => {
  const context = useContext(SalesContext);

  const [ formData, setFormData ] = useState({
    formComponents: {
      unitNumber: {
        name: 'Unit number ',
        type: 'input',
        value: context.installationAddress?.unitNumber || '',
        width: 30,
        optional: true,
        validations: {
          required: false,
          maxLength: 5,
          invalidMessage: 'Please enter a valid unit number'
        },
        config: {
          type: 'text',
          required: true,
          placeholder: '#'
        },
        isValid: true,
        isTouched: false
      },
      buildingName: {
        name: 'Complex/building name ',
        type: 'input',
        value: context.installationAddress?.buildingName || '',
        width: 70,
        optional: true,
        validations: {
          required: false
        },
        config: {
          type: 'text',
          required: true,
          maxLength: 30,
          placeholder: 'Enter complex/building name'
        },
        isValid: true,
        isTouched: false
      },
      streetNumber: {
        name: '*Street number',
        type: 'input',
        value: context.installationAddress?.streetNumber?.trim() || '',
        width: 30,
        validations: {
          required: true,
          minLength: 1,
          invalidMessage: 'Please enter a valid street number'
        },
        config: {
          type: 'text',
          required: true,
          placeholder: 'Enter street number',
          disabled: !!context.installationAddress?.streetNumber?.trim()
        },
        isValid: !!context.installationAddress?.streetNumber?.trim(),
        isTouched: true
      },
      streetName: {
        name: '*Street name',
        type: 'input',
        value: context.installationAddress?.streetName?.trim() || '',
        width: 70,
        validations: {
          required: true,
          minLength: 1,
          invalidMessage: 'Please enter a valid street name'
        },
        config: {
          type: 'text',
          required: true,
          placeholder: 'Enter street name',
          disabled: !!context.installationAddress?.streetName?.trim()
        },
        isValid: !!context.installationAddress?.streetName?.trim(),
        isTouched: true
      },
      suburb: {
        name: '*Suburb',
        type: 'input',
        value: context.installationAddress?.suburb?.trim() || '',
        width: 70,
        validations: {
          required: true,
          invalidMessage: 'Please enter a valid suburb'
        },
        config: {
          type: 'text',
          required: true,
          placeholder: 'Enter suburb',
          disabled: !!context.installationAddress.suburb?.trim()
        },
        isValid: !!context.installationAddress.suburb?.trim(),
        isTouched: true
      },
      postalCode: {
        name: '*Postal code',
        type: 'input',
        value: context.installationAddress?.postalCode?.trim() || '',
        width: 30,
        validations: {
          required: true,
          type: 'number',
          minLength: 4,
          maxLength: 5,
          invalidMessage: 'Please enter a valid postal code'
        },
        config: {
          type: 'text',
          required: true,
          placeholder: '#',
          disabled: !!context.installationAddress.postalCode?.trim(),
        },
        isValid: !!context.installationAddress.postalCode?.trim(),
        isTouched: true
      },
      city: {
        name: '*City',
        type: 'input',
        value: context.installationAddress?.city?.trim() || '',
        width: 70,
        validations: {
          required: true,
          invalidMessage: 'Please enter a valid city'
        },
        config: {
          type: 'text',
          required: true,
          placeholder: 'Enter town/city',
          disabled: !!context.installationAddress.city?.trim()
        },
        isValid: !!context.installationAddress.city?.trim(),
        isTouched: true
      },
      province: {
        name: '*Province',
        type: 'dropDown',
        value: context.installationAddress.province?.trim() || '',
        extraClass: context.installationAddress?.province?.trim() ? 'disableDropDown' : '',
        width: 30,
        validations: {
          required: true,
          invalidMessage: 'Please enter a valid province'
        },
        config: {
          type: 'text',
          required: true,
          options: provinces,
          placeholder: '- Select -'
        },
        isValid: !!context.installationAddress?.province?.trim(),
        isTouched: true
      }
    },
    isFormValid: false
  });

  const formCompArray = [];
  for (let key in formData.formComponents) {
    formCompArray.push({
      key: key,
      config: formData.formComponents[key]
    });
  }

  const formComponentChangeHandler = (value, inputIdentifier) => {
  
    const formCompsCopy = { ...formData.formComponents };
    const changedComponent = { ...formCompsCopy[inputIdentifier] };
    changedComponent.value = value;
  
    if (inputIdentifier === 'postalCode' || inputIdentifier === 'unitNumber') {
      changedComponent.value = value?.trim();
    } else {
      changedComponent.value = value;
    }
  
    if (changedComponent.validations.type === 'number') {
      let lastChar = changedComponent.value.slice(-1);
      if (isNaN(lastChar)) {
        return false;
      }
    }

    changedComponent.isValid = checkInputValidity(
      changedComponent.value,
      changedComponent.validations
    );

    changedComponent.note = changedComponent.isValid ? null : 'This is a required field.';

    const newAddress = { ...context.installationAddress, [inputIdentifier]: changedComponent.value };

    context.setInstallationAddress(newAddress);

    changedComponent.isTouched = true;

    const newFormComps = {
      ...formCompsCopy,
      [inputIdentifier]: changedComponent
    };

    const isFormValid = !Object.keys(newFormComps).find(e => newFormComps[e].isValid === false) ? true : false;
    if (isFormValid) {
      props.enableNext(true);
    } else {
      props.enableNext(false);
    }

    setFormData({
      formComponents: newFormComps,
      isFormValid: isFormValid
    });
  };

  useEffect(() => {
   
    const formCompsCopy = { ...formData.formComponents };

    for (let key in formCompsCopy) {

      const changedComponent = { ...formCompsCopy[key] };
      
      if (changedComponent.validations.required && !changedComponent.value) {

        changedComponent.note = 'This is a required field.'
      }

      formCompsCopy[key] = changedComponent;
    }

    const isFormValid = !Object.keys(formCompsCopy).find(e => formCompsCopy[e].isValid === false) ? true : false;

    if (isFormValid) {
      props.enableNext(true);
    } else {
      props.enableNext(false);
    }

    setFormData({
      formComponents: formCompsCopy,
      isFormValid: isFormValid
    });

  }, []);

  return (
    <div className="same-address-wrapper">
      {
        formCompArray.map((item, index) => {
          return (
            <div className={`delivery-field-wrapper-${ item?.config?.width }`} key={index}>
              <FormFieldLabel>
                {item.config.name}
                {
                  item?.config?.optional && <span className="vetting-optional-text"> (Optional)</span>
                }
              </FormFieldLabel>

              {
                item.config.type === 'input' &&
                  <VettingInput
                    {...item.config}
                    inputIdentifier={item.key}
                    updateValue={(value) => formComponentChangeHandler(value, item.key)}
                  />
              }
              {
                item.config.type === 'dropDown' &&
                  <VettingDropDown
                    {...item.config}
                    selectedValue={item.config?.value}
                    placeholder={item.config?.config?.placeholder}
                    options={item.config.config.options}
                    updateSelectedOption={(option) => formComponentChangeHandler(option, item.key)}
                  />
              }
            </div>
          );
        })
      }
    </div>
  );
};

InstallationAddressEditor.propTypes = {
  enableNext: PropTypes.func
};

export default InstallationAddressEditor;